/**
 * Deep merge de traducciones
 * Combina las traducciones default con las específicas del tenant,
 * permitiendo que solo se sobrescriban las claves que realmente cambian.
 *
 * @param {Array} defaultResources - Array de objetos de traducción default
 * @param {Array} tenantResources - Array de objetos de traducción del tenant (solo las que cambian)
 * @returns {Array} Array con las traducciones mergeadas
 */
export function mergeTranslations(defaultResources, tenantResources) {
  // Primero, convertimos los arrays en un objeto plano por scope/namespace
  const defaultByNamespace = convertArrayToNamespaceMap(defaultResources);
  const tenantByNamespace = convertArrayToNamespaceMap(tenantResources);

  // Hacemos el deep merge de cada namespace
  const mergedByNamespace = { ...defaultByNamespace };

  Object.keys(tenantByNamespace).forEach((namespace) => {
    if (mergedByNamespace[namespace]) {
      // Si existe en ambos, hacemos deep merge
      mergedByNamespace[namespace] = deepMerge(mergedByNamespace[namespace], tenantByNamespace[namespace]);
    } else {
      // Si solo existe en tenant, lo añadimos directamente
      mergedByNamespace[namespace] = tenantByNamespace[namespace];
    }
  });

  // Convertimos de vuelta a array de objetos
  return convertNamespaceMapToArray(mergedByNamespace);
}

/**
 * Convierte un array de objetos de traducción en un mapa por namespace
 * De: [{ auth: {...}, common: {...} }, { profile: {...} }]
 * A: { auth: {...}, common: {...}, profile: {...} }
 */
function convertArrayToNamespaceMap(resourcesArray) {
  const namespaceMap = {};

  resourcesArray.forEach((resourceObject) => {
    Object.keys(resourceObject).forEach((namespace) => {
      if (namespaceMap[namespace]) {
        // Si ya existe, hacemos merge
        namespaceMap[namespace] = deepMerge(namespaceMap[namespace], resourceObject[namespace]);
      } else {
        namespaceMap[namespace] = resourceObject[namespace];
      }
    });
  });

  return namespaceMap;
}

/**
 * Convierte un mapa de namespaces de vuelta a array de objetos
 * De: { auth: {...}, common: {...}, profile: {...} }
 * A: [{ auth: {...} }, { common: {...} }, { profile: {...} }]
 */
function convertNamespaceMapToArray(namespaceMap) {
  return Object.keys(namespaceMap).map((namespace) => ({
    [namespace]: namespaceMap[namespace],
  }));
}

/**
 * Deep merge de dos objetos
 * Las propiedades de source sobrescriben las de target
 */
function deepMerge(target, source) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key];
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }

  return output;
}

/**
 * Verifica si un valor es un objeto plano (no array, no null)
 */
function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}
